/**
 * Central seam for Inertia page component resolution. The `@inertiajs/vite`
 * plugin generates a resolver from `pages: "../pages"` with no error path:
 * at boot a rejected import lands in the entrypoint catch, during a
 * client-side navigation it dies as an unhandled rejection with a broken
 * page behind it (Sentry AMEALIO-FRONTEND-5/-6). Passing an explicit
 * `resolve` keeps the plugin from rewriting and puts every page load — boot
 * and navigation — through the wrapper below.
 */

export type PageLoader = () => Promise<unknown>;

export type CreatePageResolverOptions = {
  /**
   * The `import.meta.glob` map from the entrypoint. Keys are relative to
   * that file, mirroring the plugin's generated lookup: `../pages/<name>` +
   * one of the extensions.
   */
  loaders: Record<string, PageLoader>;
  /** Tried in order, mirroring the plugin's React defaults. */
  extensions: string[];
  /**
   * Called once per failed load with the wrapped error, before it is
   * rethrown. The entrypoint decides the recovery: boot failures flow into
   * the existing catch, navigation failures would otherwise go unreported.
   */
  onLoadFailure: (error: Error, page: string) => void;
};

/**
 * Resolves a component name to its page module, like the generated plugin
 * resolver — plus a failure path: a rejected import is wrapped in an error
 * naming the page (the original rides along as `cause`, so Sentry shows the
 * actual parse/fetch error) and reported through `onLoadFailure`. The type
 * parameter is the caller's module contract (the entrypoint pins it to
 * Inertia's `ResolvedComponent`); the wrapper only transports the module.
 */
export function createPageResolver<T = unknown>({
  loaders,
  extensions,
  onLoadFailure,
}: CreatePageResolverOptions): (name: string) => Promise<T> {
  return async (name: string) => {
    const loader = extensions
      .map((extension) => loaders[`../pages/${name}${extension}`])
      .find((candidate) => candidate !== undefined);

    if (!loader) {
      throw new Error(`Page not found: ${name}`);
    }

    try {
      return (await loader()) as T;
    } catch (error) {
      const wrapped = new Error(`Page component failed to load: ${name}`, {
        cause: error,
      });
      onLoadFailure(wrapped, name);
      throw wrapped;
    }
  };
}
